// ============================================================================
// QuotesEditorPanel — /admin → "Quotes" tab
// ----------------------------------------------------------------------------
// Manage the rotating quotes shown on the marketing home page.
//
// Features
//   • List all quotes with inline edit, delete, and active/hidden toggle
//   • Add a single quote with attribution
//   • Bulk-paste import — one line per quote, optional " | attribution"
//   • Drag-free reorder via up/down arrows (avoids adding a DnD dep)
//   • Live preview of what visitors will see, cross-fading every 4s
// ============================================================================

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Save, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { RotatingQuotes } from "./RotatingQuotes";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuotesEditorPanel = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addText, setAddText] = useState("");
  const [addAttr, setAddAttr] = useState("");
  const [bulkRaw, setBulkRaw] = useState("");
  const [bulkReplace, setBulkReplace] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [drafts, setDrafts] = useState({}); // id -> {text, attribution}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quotes`);
      setQuotes(res.data?.quotes || []);
      // Bump the preview so it re-fetches the public feed.
      setPreviewKey((k) => k + 1);
    } catch (err) {
      logger.error("Failed to load quotes", err);
      toast.error("Couldn't load quotes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addOne = async () => {
    const text = addText.trim();
    if (!text) return;
    try {
      const nextOrder = quotes.length ? Math.max(...quotes.map(q => q.order ?? 0)) + 1 : 0;
      await axios.post(`${API}/admin/quotes`, {
        text,
        attribution: addAttr.trim(),
        order: nextOrder,
        active: true,
      });
      setAddText(""); setAddAttr("");
      toast.success("Quote added.");
      refresh();
    } catch (err) {
      logger.error("Add quote failed", err);
      toast.error("Couldn't add quote.");
    }
  };

  const bulkImport = async () => {
    const raw = bulkRaw.trim();
    if (!raw) return;
    try {
      const res = await axios.post(`${API}/admin/quotes/bulk-import`, {
        raw, replace_all: bulkReplace,
      });
      const n = res.data?.inserted ?? 0;
      toast.success(`${n} ${n === 1 ? "quote" : "quotes"} imported.`);
      setBulkRaw("");
      setBulkReplace(false);
      refresh();
    } catch (err) {
      logger.error("Bulk import failed", err);
      toast.error("Bulk import failed.");
    }
  };

  const toggleActive = async (q) => {
    try {
      await axios.put(`${API}/admin/quotes/${q.id}`, { active: !q.active });
      refresh();
    } catch (err) {
      logger.error("Toggle active failed", err);
      toast.error("Couldn't update quote.");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete this quote?\n\n"${(q.text || "").slice(0, 90)}..."`)) return;
    try {
      await axios.delete(`${API}/admin/quotes/${q.id}`);
      toast.success("Deleted.");
      refresh();
    } catch (err) {
      logger.error("Delete quote failed", err);
      toast.error("Couldn't delete quote.");
    }
  };

  const move = async (q, dir) => {
    const idx = quotes.findIndex((x) => x.id === q.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= quotes.length) return;
    const ids = quotes.map((x) => x.id);
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    try {
      await axios.post(`${API}/admin/quotes/reorder`, { ids });
      refresh();
    } catch (err) {
      logger.error("Reorder failed", err);
      toast.error("Couldn't reorder.");
    }
  };

  const saveDraft = async (q) => {
    const d = drafts[q.id];
    if (!d) return;
    const payload = {};
    if (d.text !== undefined && d.text !== q.text) payload.text = d.text;
    if (d.attribution !== undefined && d.attribution !== q.attribution) payload.attribution = d.attribution;
    if (!Object.keys(payload).length) {
      toast.info("No changes to save.");
      return;
    }
    try {
      await axios.put(`${API}/admin/quotes/${q.id}`, payload);
      setDrafts((prev) => { const n = { ...prev }; delete n[q.id]; return n; });
      toast.success("Saved.");
      refresh();
    } catch (err) {
      logger.error("Save quote failed", err);
      toast.error("Couldn't save.");
    }
  };

  return (
    <div className="space-y-6" data-testid="quotes-editor-panel">
      {/* Live preview */}
      <div className="bg-[#FBF8F1] rounded-2xl border border-slate-100 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#718096]">
            Live preview
          </h3>
          <span className="text-xs text-[#A0A6B4]">
            {quotes.filter(q => q.active).length} active · rotates every 4s
          </span>
        </div>
        {/* key forces remount → refetch after edits; bypassCache=true so
            we never read the stale localStorage snapshot inside admin. */}
        <RotatingQuotes key={previewKey} testId="quotes-editor-preview" bypassCache />
      </div>

      {/* Add one */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
        <h3 className="text-base font-semibold text-[#1B2349] mb-4">Add a quote</h3>
        <div className="space-y-3">
          <Textarea
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            placeholder="The quote text — no need to include quote marks."
            className="min-h-[80px]"
            data-testid="add-quote-text"
          />
          <Input
            value={addAttr}
            onChange={(e) => setAddAttr(e.target.value)}
            placeholder="Attribution (optional) — e.g. — Bilateral Calm member"
            data-testid="add-quote-attribution"
          />
          <div>
            <Button
              onClick={addOne}
              disabled={!addText.trim()}
              className="bg-[#3090A8] hover:bg-[#267B90] text-white rounded-full"
              data-testid="add-quote-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Add quote
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk paste */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
        <h3 className="text-base font-semibold text-[#1B2349] mb-2">Bulk import</h3>
        <p className="text-sm text-[#718096] mb-4">
          Paste multiple quotes — one per line. Use <code className="bg-[#F6F4EF] px-1.5 py-0.5 rounded text-xs">|</code> to
          add an attribution:<br />
          <code className="text-xs text-[#4A5568]">
            &ldquo;The app helped me sleep for the first time in months.&rdquo; | — Rachel, member
          </code>
        </p>
        <Textarea
          value={bulkRaw}
          onChange={(e) => setBulkRaw(e.target.value)}
          placeholder={`This app changed my nights. | — Rachel M.\nI stopped panicking on my train commute. | — Alex\nQuiet mind. Finally.`}
          className="min-h-[140px] font-mono text-sm"
          data-testid="bulk-import-textarea"
        />
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-[#4A5568] cursor-pointer">
            <input
              type="checkbox"
              checked={bulkReplace}
              onChange={(e) => setBulkReplace(e.target.checked)}
              data-testid="bulk-replace-checkbox"
            />
            <span>Hide all existing quotes first (replaces the list)</span>
          </label>
          <Button
            onClick={bulkImport}
            disabled={!bulkRaw.trim()}
            className="bg-[#3090A8] hover:bg-[#267B90] text-white rounded-full"
            data-testid="bulk-import-button"
          >
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[#1B2349]">
            All quotes ({quotes.length})
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            data-testid="refresh-quotes"
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-[#718096]">Loading…</p>
        ) : !quotes.length ? (
          <p className="text-sm text-[#A0A6B4]">
            No quotes yet — add one above, or bulk-paste a batch.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="quotes-list">
            {quotes.map((q, i) => {
              const draft = drafts[q.id] || {};
              const draftText = draft.text ?? q.text;
              const draftAttr = draft.attribution ?? q.attribution ?? "";
              const dirty = draftText !== q.text || draftAttr !== (q.attribution || "");
              return (
                <li key={q.id} className="py-4 flex flex-col sm:flex-row gap-3" data-testid={`quote-row-${q.id}`}>
                  <div className="flex sm:flex-col gap-1 shrink-0">
                    <button
                      className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center disabled:opacity-30"
                      onClick={() => move(q, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center disabled:opacity-30"
                      onClick={() => move(q, 1)}
                      disabled={i === quotes.length - 1}
                      aria-label="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0 space-y-2">
                    <Textarea
                      value={draftText}
                      onChange={(e) => setDrafts((prev) => ({
                        ...prev, [q.id]: { ...prev[q.id], text: e.target.value },
                      }))}
                      className="min-h-[64px] text-[15px]"
                      data-testid={`quote-text-${q.id}`}
                    />
                    <Input
                      value={draftAttr}
                      onChange={(e) => setDrafts((prev) => ({
                        ...prev, [q.id]: { ...prev[q.id], attribution: e.target.value },
                      }))}
                      placeholder="Attribution (optional)"
                      className="text-sm"
                      data-testid={`quote-attr-${q.id}`}
                    />
                  </div>

                  <div className="flex sm:flex-col gap-2 shrink-0">
                    <Button
                      variant={dirty ? "default" : "outline"}
                      size="sm"
                      onClick={() => saveDraft(q)}
                      disabled={!dirty}
                      className={dirty ? "bg-[#3090A8] hover:bg-[#267B90] text-white rounded-full" : "rounded-full"}
                      data-testid={`save-quote-${q.id}`}
                    >
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(q)}
                      className="rounded-full"
                      data-testid={`toggle-quote-${q.id}`}
                    >
                      {q.active ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
                      {q.active ? "Live" : "Hidden"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => remove(q)}
                      className="rounded-full text-[#D98880] border-[#D98880]/40 hover:bg-[#D98880]/10"
                      data-testid={`delete-quote-${q.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default QuotesEditorPanel;
